"use client";

import type { NewsLogo } from "./news-types";

import BloombergLogo from "@homeinstead/icons/BloombergLogo";
import FastCompanyLogo from "@homeinstead/icons/FastCompanyLogo";
import ParadeLogo from "@homeinstead/icons/ParadeLogo";
import PeopleLogo from "@homeinstead/icons/PeopleLogo";
import TheHuffingtonPostLogo from "@homeinstead/icons/TheHuffingtonPostLogo";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";
import { FeaturedNewsLogo } from "./FeaturedNewsLogo";
import { FeaturedOnRow, FeaturedOnStack } from "./FeaturedOn";
import ForbesLogo from "./forbes.svg";
import NewsWeek from "./newsweek.svg";
import TCLogo from "./tc.svg";
import USNews from "./us-news.svg";
import WSJLogo from "./wsj.svg";

// News logo data
const newsLogos: NewsLogo[] = [
  {
    className: "object-left",
    Icon: ForbesLogo,
    title: "Forbes",
    width: "w-fit",
  },
  { Icon: TCLogo, title: "TC", width: "w-fit" },
  {
    className: "object-left",
    Icon: WSJLogo,
    title: "Wall Street Journal",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: NewsWeek,
    title: "News Week",
    width: "w-fit",
  },
  { className: "object-left", Icon: USNews, title: "US News", width: "w-fit" },
  {
    className: "object-left",
    Icon: PeopleLogo,
    title: "People",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: BloombergLogo,
    title: "Bloomberg",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: ParadeLogo,
    title: "Parade",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: FastCompanyLogo,
    title: "Fast Company",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: TheHuffingtonPostLogo,
    title: "The Huffington Post",
    width: "w-fit",
  },
];

/**
 * @deprecated Use an FeaturedNewsLogos from `@homeinstead/ui/components/FeaturedNewsLogos` instead
 */
export default function FeaturedNewsLogos({
  animateAlways = true,
}: {
  animateAlways?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const logoSetRef = useRef<HTMLDivElement>(null);
  const [animationWidth, setAnimationWidth] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(animateAlways);

  const containerEntry = useResizeObserver({
    ref: containerRef as React.RefObject<HTMLElement>,
  });

  useEffect(() => {
    if (containerRef.current && logoSetRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const logoRowWidth = logoSetRef.current.offsetWidth;
      // TODO stop suppressing this warning (don't replicate this!)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShouldAnimate(animateAlways || logoRowWidth > containerWidth);
      setAnimationWidth(logoRowWidth);
    }
  }, [animateAlways, containerEntry.width]);

  return (
    <div className="min-h-24 w-full py-8 xl:pl-[152px]">
      <div className="mx-auto w-full overflow-hidden" ref={containerRef}>
        <FeaturedOnStack />
        <div className="mx-auto flex w-full flex-col">
          <div className="w-full lg:flex lg:items-center lg:gap-4">
            <FeaturedOnRow />
            <div className="overflow-hidden">
              <motion.div
                animate={shouldAnimate ? { x: [0, -animationWidth - 40] } : {}}
                // note that the animation width has a 24px offset to account for the gap-20 (gap-[24px] styling above)
                className="flex h-full items-center gap-20"
                transition={
                  shouldAnimate
                    ? {
                        x: {
                          duration: 30,
                          ease: "linear",
                          repeat: Infinity,
                          repeatType: "loop",
                        },
                      }
                    : {}
                }
              >
                {/* Measurable first row */}
                <div
                  className="flex shrink-0 items-center gap-20"
                  ref={logoSetRef}
                >
                  {newsLogos.map((logo, index) => (
                    <FeaturedNewsLogo key={`logo-${index}`} {...logo} />
                  ))}
                </div>

                {/* Duplicated row for seamless loop */}
                <div
                  aria-hidden="true"
                  className="flex shrink-0 items-center gap-20"
                >
                  {newsLogos.map((logo, index) => (
                    <FeaturedNewsLogo key={`logo-dup-${index}`} {...logo} />
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
